<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConfirm } from 'primevue/useconfirm'
import ProgressSpinner from 'primevue/progressspinner'
import GbButton from '@/components/GbButton.vue'
import { AccessPointsUtil } from '@/util/access_points'
import { betaOptIn, isBetaOptIn, withBetaOptIn } from '@/util/beta_opt_in'
import { toolkitUrls } from '@/util/links'
import AdminConfirmDialog from './AdminConfirmDialog.vue'
import AdminErrorToasts from './AdminErrorToasts.vue'
import DraftActions from './DraftActions.vue'
import EditAccessPoint from './EditAccessPoint.vue'
import EditObjective from './EditObjective.vue'
import EditScore from './EditScore.vue'
import EditTitle from './EditTitle.vue'
import SelectCharacteristics from './SelectCharacteristics.vue'
import SelectGrades from './SelectGrades.vue'
import SelectResources from './SelectResources.vue'
import SelectStandards from './SelectStandards.vue'
import SelectStrategies from './SelectStrategies.vue'
import SelectSubjects, { type SubjectAlignment } from './SelectSubjects.vue'
import type { EditableAccessPoint, EditableObjective } from './anchor_page_editor_api'
import { useAnchorPageEditor } from './use_anchor_page_editor'

const CONFIRM_GROUP = 'edit-anchor-page'

const props = defineProps<{ uuid: string }>()

const route = useRoute()
const router = useRouter()
const confirm = useConfirm()
const editor = useAnchorPageEditor(() => props.uuid)
const { common, accessPoints, isLoading, isDraft, isSaving } = editor

// The View link opens in a new tab, which only reaches Vue 3 if it carries the opt-in.
const viewUrl = computed(() => {
  const url = toolkitUrls.anchorPage(props.uuid)
  return isBetaOptIn(route.query) ? withBetaOptIn(url) : url
})

const gradeOptions = computed(() => editor.subjectGradesMap.value[common.value.subject_alignments[0]?.subject] ?? [])

async function load() {
  const { legacyGoalUuid, gradeLevelUuid } = await editor.load()
  if (legacyGoalUuid) {
    window.location.href = toolkitUrls.editorLinks.editGoal(legacyGoalUuid)
  } else if (gradeLevelUuid) {
    router.replace({ name: 'EditAnchorAccessPoints', params: { uuid: gradeLevelUuid }, query: betaOptIn(route.query) })
  }
}

onMounted(() => {
  load()
  editor.loadOptions()
})
watch(() => props.uuid, load)

function setCommon<K extends keyof typeof common.value>(field: K, value: typeof common.value[K], flushImmediately = false) {
  common.value = { ...common.value, [field]: value }
  editor.saveCommonData({ [field]: value }, flushImmediately)
}

function updateAccessPointText(accessPoint: EditableAccessPoint, text: string) {
  accessPoint.goal_text = text
  editor.pushUpdate({ uuids: [accessPoint.uuid], updates: { goal_text: text } })
}

function updateGoalResources(accessPoint: EditableAccessPoint, uuids: string[], languages?: string[]) {
  accessPoint.goal_resources = uuids
  // A reorder sends no languages; leaving the key out keeps a pending language change from being wiped.
  const updates = languages === undefined ? { goal_resources: uuids } : { goal_resources: uuids, resource_languages: languages }
  editor.pushUpdate({ uuids: [accessPoint.uuid], updates })
}

function updateObjective(accessPoint: EditableAccessPoint, objective: EditableObjective, changes: Partial<EditableObjective>) {
  Object.assign(objective, changes)
  editor.saveObjectives(accessPoint)
}

function confirmThen(header: string, message: string, accept: () => void, icon?: string) {
  confirm.require({ group: CONFIRM_GROUP, header, message, icon, accept })
}

function togglePublish(isPublished: boolean) {
  if (!isDraft.value || isPublished) {
    editor.setPublished(isPublished)
    return
  }
  confirmThen(
    'Unpublish Goal',
    'Draft changes will be lost when unpublishing. You must save draft changes before unpublishing if you wish to keep them. Do you wish to proceed?',
    async () => {
      if (!(await editor.discardDraft())) return
      editor.setPublished(false)
      await editor.flushUpdates()
      load()
    },
    'warning_amber'
  )
}

function clearObjectives(accessPoint: EditableAccessPoint) {
  if (!accessPoint.objectives?.length) return
  confirmThen('Remove Objectives', 'Are you sure you want to remove objectives for this access point?', () => editor.clearObjectives(accessPoint))
}

async function saveDraft() {
  await editor.flushUpdates()
  if (await editor.saveDraft()) load()
}

async function discardDraft() {
  await editor.flushUpdates()
  if (await editor.discardDraft()) load()
}

async function generateMissingAccessPoints() {
  if (await editor.generateMissingAccessPoints()) load()
}
</script>

<template>
  <div>
    <div v-if="!isLoading" class="sticky top-0 z-[999] bg-white border-b border-gray-400">
      <div class="container flex items-center gap-2 py-2">
        <a :href="viewUrl" target="_blank" class="inline-flex items-center h-10 px-4 text-md text-white bg-primary-400 rounded-xs focus-visible:outline-primary-400">View</a>
        <GbButton v-if="!common.published" gbTheme="greenPrimary" label="Publish" :disabled="isLoading" @click="togglePublish(true)" />
        <GbButton v-else gbTheme="redPrimary" label="Unpublish" class="!bg-orange-300 hover:brightness-90 focus-visible:!ring-orange-300" :disabled="isLoading" @click="togglePublish(false)" />
        <span class="text-gray-700" role="status">
          <template v-if="common.published">Last published {{ common.published_at }}</template>
          <template v-if="(!isDraft || !common.published) && isSaving"><ProgressSpinner class="h-4 w-4" aria-hidden="true" /> <i>Saving...</i></template>
          <template v-if="!common.published && !isSaving">Last saved {{ editor.mostRecentUpdatedAt.value }}</template>
        </span>
        <template v-if="isDraft">
          <DraftActions :uuid="uuid" class="ml-auto" @save-draft="saveDraft" @discard-draft="discardDraft" />
          <span class="text-gray-700" role="status">
            <template v-if="isSaving"><ProgressSpinner class="h-4 w-4" aria-hidden="true" /> <i>Saving...</i></template>
            <template v-else>Last saved {{ editor.mostRecentUpdatedAt.value }}</template>
          </span>
        </template>
      </div>
    </div>

    <div class="container mt-4">
      <h1 class="sr-only">Edit Anchor Access Points</h1>
      <p v-if="isLoading" class="text-gray-700"><ProgressSpinner class="h-4 w-4" aria-hidden="true" /> Loading...</p>
      <div v-else class="grid gap-6 md:grid-cols-12">
        <div class="min-w-0 md:col-span-4">
          <h2 class="mb-2 header-md">Subjects and Domains</h2>
          <SelectSubjects
            :subject-alignments="common.subject_alignments"
            :subjects="editor.subjects.value"
            :subject-domains-map="editor.subjectDomainsMap.value"
            @update:subject-alignments="(alignments: SubjectAlignment[]) => setCommon('subject_alignments', alignments)"
          />
          <SelectGrades
            :disabled="!common.subject_alignments.length"
            :options="gradeOptions"
            :selected="common.grades"
            @update:selected="grades => setCommon('grades', grades)"
          />
          <SelectCharacteristics
            :id="`${uuid}-characteristics-typeahead`"
            :selected="common.characteristics"
            :characteristics-map="editor.characteristicsMap.value"
            @update:selected="uuids => setCommon('characteristics', uuids)"
            @add="editor.populateCharacteristicsMap"
            @load-error="editor.notifyError"
          />

          <h2 class="mt-4 mb-2 header-md">Standards</h2>
          <label class="flex items-center gap-2 mb-2">
            <input
              id="universal-checkbox"
              type="checkbox"
              :checked="Boolean(common.universal)"
              @change="event => setCommon('universal', (event.target as HTMLInputElement).checked)"
            >
            Mark as Universal
          </label>
          <SelectStandards
            v-for="setCode in editor.validStandardSets.value"
            :id="`${setCode}-std-typeahead`"
            :key="setCode"
            :set-code="setCode"
            :selected="editor.standardsForSet.value[setCode]"
            :standards-map="editor.standardsMap.value"
            @update:selected="refs => editor.updateStandardsForSet(setCode, refs)"
          />
        </div>

        <div class="min-w-0 md:col-span-8">
          <EditTitle :model-value="common.title" @update:model-value="title => setCommon('title', title)" />
          <div class="grid grid-cols-2 gap-6">
            <EditScore :model-value="common.score" @update:model-value="score => setCommon('score', score)" />
            <SelectStrategies
              :id="`${uuid}-select-strategies`"
              :selected="common.adaptation_strategies"
              :strategies-map="editor.strategiesMap.value"
              @update:selected="uuids => setCommon('adaptation_strategies', uuids)"
              @add="editor.populateStrategiesMap"
              @load-error="editor.notifyError"
            />
          </div>

          <h2 class="mt-4 mb-2 header-md">Access Points</h2>
          <p class="max-w-none mb-4 text-gray-700">
            Embed a link to a strategy with the following wiki syntax: <code>[[ strategy-name | text-to-show ]], e.g. [[Animations|animated models]]</code>.
            Include links to other websites with the following wiki syntax: <code>[[ https://goalbook.com/toolkit | text to display ]]</code>.
          </p>

          <EditAccessPoint
            v-for="accessPoint in accessPoints"
            :key="accessPoint.uuid"
            :uuid="accessPoint.uuid"
            :level="accessPoint.access_point_level"
            :text="accessPoint.goal_text"
            @update:text="text => updateAccessPointText(accessPoint, text)"
          >
            <SelectResources
              :id="accessPoint.uuid"
              :ap-level="accessPoint.access_point_level"
              goal-type="annual_target"
              :selected="accessPoint.goal_resources"
              :resources-map="editor.resourcesMap.value"
              @update:selected="(uuids, languages) => updateGoalResources(accessPoint, uuids, languages)"
              @add="(resourceUuid, languages) => editor.addLanguage(resourceUuid, accessPoint.uuid, languages)"
            />

            <h4 class="flex items-center gap-4 -ml-8 mb-4 text-lg font-medium">
              Quarterly Objectives | {{ AccessPointsUtil.labelsMap[accessPoint.access_point_level] }}
              <GbButton
                v-if="!accessPoint.objectives?.length"
                gbTheme="grayOutlineSecondary"
                label="Generate quarterly objectives"
                @click="editor.generateObjectives(accessPoint)"
              />
              <GbButton v-else gbTheme="grayOutlineSecondary" label="Remove objectives" @click="clearObjectives(accessPoint)" />
            </h4>

            <EditObjective
              v-for="(objective, index) in accessPoint.objectives"
              :key="objective.level"
              :goal-uuid="accessPoint.uuid"
              :level="objective.level"
              :ap-level="accessPoint.access_point_level"
              :title="objective.title"
              :text="objective.text"
              @update:title="title => updateObjective(accessPoint, objective, { title })"
              @update:text="text => updateObjective(accessPoint, objective, { text })"
            >
              <SelectResources
                :id="`${accessPoint.uuid}-${objective.level}-resources`"
                :obj-level="objective.level"
                :ap-level="accessPoint.access_point_level"
                goal-type="quarterly_objective"
                :selected="objective.resources"
                :resources-map="editor.resourcesMap.value"
                @update:selected="uuids => updateObjective(accessPoint, objective, { resources: uuids })"
                @add="editor.populateResourcesMap"
              />
              <hr v-if="index !== accessPoint.objectives!.length - 1" class="my-4 border-gray-300">
            </EditObjective>
          </EditAccessPoint>

          <GbButton
            v-if="editor.areAccessPointsMissing.value"
            gbTheme="bluePrimary"
            label="Generate missing access points"
            @click="generateMissingAccessPoints"
          />
        </div>
      </div>
    </div>
    <AdminConfirmDialog :group="CONFIRM_GROUP" />
    <AdminErrorToasts :errors="editor.errors.value" @dismiss="editor.dismissError" />
  </div>
</template>
